import { css, FlattenSimpleInterpolation } from 'styled-components';
import { rgba } from '../core/Color';
import { remCalc } from '../core/Style';
import { toShorthandSize } from './propTypes/tshirtSize';
import {
  INPUT_VERTICAL_ALIGNMENT,
  WEB_FONT_FAMILY,
  WEB_FONT_BOLD_WEIGHT,
  WEB_FONT_DEMI_BOLD_WEIGHT,
  WEB_FONT_MEDIUM_WEIGHT,
  WEB_FONT_ITALIC_WEIGHT,
  WEB_FONT_REGULAR_WEIGHT,
} from 'HubStyleTokens/misc';
import {
  BASE_SHADOW_ALPHA,
  BASE_SHADOW_BLUR_RADIUS,
  BASE_SHADOW_OFFSET_Y,
  BUTTON_RADIUS,
  BUTTON_DEFAULT_PADDING_Y,
  BUTTON_DEFAULT_PADDING_X,
  GRID_BREAKPOINT_SMALL,
  DISTANCE_MEASUREMENT_EXTRA_SMALL,
  DISTANCE_MEASUREMENT_SMALL,
  DISTANCE_MEASUREMENT_MEDIUM,
  DISTANCE_MEASUREMENT_LARGE,
  DISTANCE_MEASUREMENT_EXTRA_LARGE,
  LAYOUT_MAX_WIDTH,
  LAYOUT_PADDING_X,
  FOCUS_RING_SHADOW_ALPHA,
  FOCUS_RING_SHADOW_SIZES,
  FOCUS_RING_SHADOW_LAST_SIZES,
  INPUT_DEFAULT_HEIGHT,
  INPUT_BORDER_WIDTH,
  INPUT_FONT_SIZE,
} from 'HubStyleTokens/sizes';
import { FOCUS_RING_BASE } from 'HubStyleTokens/colors';
import {
  BASE_SHADOW_COLOR,
  FORM_CONTROL_FOREGROUND_COLOR,
  FORM_CONTROL_BACKGROUND_COLOR,
  FORM_CONTROL_BORDER_COLOR,
} from 'HubStyleTokens/theme';
import { MICRO_TRANSITION_TIMING } from 'HubStyleTokens/times';

type DistanceKeys = 'flush' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export const setFontSize = (size: string): FlattenSimpleInterpolation => css`
  font-size: ${remCalc(size)};
`;

export const setFontSmoothing = (smoothing?: string): string => {
  const computedSmoothing = smoothing || 'antialiased';

  // No `css` wrapper for this mixin, due to a bug in styled-components v3
  return `
    -webkit-font-smoothing: ${computedSmoothing};
    -moz-osx-font-smoothing: ${
      computedSmoothing === 'antialiased' ? 'grayscale' : computedSmoothing
    };
  `;
};

// note: just a template literal, not using css function
export const setFontStack = (family: string): string => `
  font-family: ${family};
`;

export const FONT_FAMILIES = {
  bold: () => {
    return `
        ${setFontStack(WEB_FONT_FAMILY)};
        font-weight: ${WEB_FONT_BOLD_WEIGHT};
      `;
  },
  demibold: () => {
    return `
        ${setFontStack(WEB_FONT_FAMILY)};
        font-weight: ${WEB_FONT_DEMI_BOLD_WEIGHT};
      `;
  },
  medium: () => {
    return `
        ${setFontStack(WEB_FONT_FAMILY)};
        font-weight: ${WEB_FONT_MEDIUM_WEIGHT};
      `;
  },
  italic: () => {
    return `
        ${setFontStack(WEB_FONT_FAMILY)};
        font-style: italic;
        font-weight: ${WEB_FONT_ITALIC_WEIGHT};
      `;
  },
  default: () => {
    return `
        ${setFontStack(WEB_FONT_FAMILY)};
        font-weight: ${WEB_FONT_REGULAR_WEIGHT};
      `;
  },
};

export const setUiTransition = (
  property?: string,
  timing?: string
): FlattenSimpleInterpolation => {
  const computedTiming = timing || MICRO_TRANSITION_TIMING;
  const computedProperty = property || 'all';

  return css`
    transition: ${computedProperty} ${computedTiming} ease-out;
  `;
};

/**
 * A mixin that simulates focus styling. Needed when the semantic element with the focus is hidden,
 * such as the `<input>` in `UICheckbox`.
 */
export const uiFocus: FlattenSimpleInterpolation = css`
  box-shadow:
    ${FOCUS_RING_SHADOW_SIZES}
      ${rgba(FOCUS_RING_BASE, parseFloat(FOCUS_RING_SHADOW_ALPHA))},
    ${FOCUS_RING_SHADOW_LAST_SIZES} ${FOCUS_RING_BASE} !important;
`;

export const uiLayerShadow = (): FlattenSimpleInterpolation => css`
  box-shadow: 0 ${BASE_SHADOW_OFFSET_Y} ${BASE_SHADOW_BLUR_RADIUS} 0
    ${rgba(BASE_SHADOW_COLOR, parseFloat(BASE_SHADOW_ALPHA))};
`;

export const toPx = (value: number | string): string =>
  typeof value === 'number' ? `${value}px` : value;

export const toPercent = (value: number): string | undefined =>
  value == null ? undefined : `${value}%`;

export const percentToVW = (width: string): string => {
  if (typeof width !== 'string') return width;
  return width.replace(/(\d+)%/g, '$1vw');
};

export const setBorderRadius = (
  radius: string = BUTTON_RADIUS
): FlattenSimpleInterpolation => css`
  border-radius: ${toPx(radius)};
`;

export const setGrabCursor = (
  cursor = 'grab'
): FlattenSimpleInterpolation => css`
  cursor: move; /* Fallback for IE11 */
  cursor: ${cursor};
`;

export const setInputAppearance = (): FlattenSimpleInterpolation => css`
  display: block;
  height: ${INPUT_DEFAULT_HEIGHT};
  width: 100%;
  font-size: ${INPUT_FONT_SIZE};
  line-height: 22px;
  text-align: left;
  vertical-align: ${INPUT_VERTICAL_ALIGNMENT};
  color: ${FORM_CONTROL_FOREGROUND_COLOR};
  background-color: ${FORM_CONTROL_BACKGROUND_COLOR};
  border: ${INPUT_BORDER_WIDTH} solid ${FORM_CONTROL_BORDER_COLOR};
  border-radius: ${BUTTON_RADIUS};
`;

export const setInputMetrics = (
  padVertical: string = BUTTON_DEFAULT_PADDING_Y,
  padHorizontal: string = BUTTON_DEFAULT_PADDING_X,
  padRight: string = padHorizontal,
  padBottom: string = padVertical,
  responsive = true
): FlattenSimpleInterpolation => {
  return css`
    padding-bottom: ${toPx(padBottom)};
    padding-left: ${toPx(padHorizontal)};
    padding-right: ${toPx(padRight)};
    padding-top: ${toPx(padVertical)};
    ${responsive
      ? css`
          @media only screen and (max-width: ${GRID_BREAKPOINT_SMALL}) {
            width: 100%;
          }
        `
      : null};
  `;
};

const DISTANCE_SIZES: Record<DistanceKeys, string | number> = {
  flush: 0,
  xs: DISTANCE_MEASUREMENT_EXTRA_SMALL,
  sm: DISTANCE_MEASUREMENT_SMALL,
  md: DISTANCE_MEASUREMENT_MEDIUM,
  lg: DISTANCE_MEASUREMENT_LARGE,
  xl: DISTANCE_MEASUREMENT_EXTRA_LARGE,
};

export const setDistance = (distance = 'md'): FlattenSimpleInterpolation => {
  const size = DISTANCE_SIZES[toShorthandSize(distance) as DistanceKeys];
  return css`
    margin-top: ${size};
    margin-bottom: ${size};
  `;
};

/**
 * Given styles, wraps them in selectors to apply to placeholder text in an input or textarea.
 */
export const getPlaceholderStyles = (
  styles: FlattenSimpleInterpolation
): FlattenSimpleInterpolation => css`
  &::-moz-placeholder {
    opacity: 1;
    ${styles};
  }
  &:-ms-input-placeholder {
    ${styles};
  }
  &::-webkit-input-placeholder {
    ${styles};
  }
`;

/**
 * @returns a mixin that sets the appropriate width and margins to match a page template with
 * `pageLayout="max-width"`.
 */
export const setTemplateMaxWidth = (): FlattenSimpleInterpolation => css`
  margin-left: auto;
  margin-right: auto;
  max-width: ${toPx(
    parseInt(LAYOUT_MAX_WIDTH, 10) + parseInt(LAYOUT_PADDING_X, 10) * 2
  )};
`;
